import { twMerge } from "tailwind-merge";
import { useState } from "react";
import { Copy, Check } from "lucide-react";
import { Button } from "@shared/ui/Button";

export type Props = {
  onCopy?: () => void;
  label?: string;
  className?: string;
  textToCopy?: string;
  type?: "rich" | "plain";
  sectionName?: string;
};

export function ClipboardCopyButton({
  onCopy,
  label = "Copy",
  className,
  textToCopy = "",
  type = "plain",
  sectionName,
}: Props) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    // Call the onCopy callback if provided
    onCopy?.();

    // For plain text, HTML, links, and code, use the clipboard API
    if (type === "plain") {
      navigator.clipboard.writeText(textToCopy);

      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 2000);

      return;
    }

    // Rich text copying with background removal
    if (type === "rich") {
      const elementToCopy = document.querySelector(
        `[data-copy-content="${sectionName}"]`
      );
      if (!elementToCopy) return;

      // Store elements that had background styles
      const parentsWithBg: Array<{
        element: HTMLElement;
        computedBg: string;
        computedBgColor: string;
        originalStyle: {
          background: string;
          backgroundColor: string;
        };
        originalClasses: string[];
      }> = [];

      // Walk up the tree and store/remove background styles
      let currentEl: HTMLElement | null = elementToCopy as HTMLElement;
      while (currentEl) {
        // Check for background styles or classes
        const computedStyle = window.getComputedStyle(currentEl);
        const hasBg =
          computedStyle.background !== "none" ||
          computedStyle.backgroundColor !== "rgba(0, 0, 0, 0)";
        const hasBackgroundClass = Array.from(currentEl.classList).some((cls) =>
          cls.includes("bg-")
        );

        if (hasBg || hasBackgroundClass) {
          // Store both computed and inline styles
          parentsWithBg.push({
            element: currentEl,
            computedBg: computedStyle.background,
            computedBgColor: computedStyle.backgroundColor,
            originalStyle: {
              background: currentEl.style.background,
              backgroundColor: currentEl.style.backgroundColor,
            },
            originalClasses: Array.from(currentEl.classList),
          });

          // Remove background styles
          currentEl.style.setProperty("background", "none", "important");
          currentEl.style.setProperty(
            "background-color",
            "transparent",
            "important"
          );

          // Remove background classes
          const classesToRemove = Array.from(currentEl.classList).filter(
            (cls) => cls.includes("bg-")
          );
          currentEl.classList.remove(...classesToRemove);
        }
        currentEl = currentEl.parentElement;
      }

      // Do the copy
      try {
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(elementToCopy);
        selection?.removeAllRanges();
        selection?.addRange(range);
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy rich text:", err);
      } finally {
        // Clean up selection
        window.getSelection()?.removeAllRanges();

        // Restore background styles - do this in finally to ensure it happens even if copy fails
        parentsWithBg.forEach(
          ({
            element,
            computedBg,
            computedBgColor,
            originalStyle,
            originalClasses,
          }) => {
            // First remove our temporary overrides
            element.style.removeProperty("background");
            element.style.removeProperty("background-color");

            // Restore original inline styles if they existed
            if (originalStyle.background)
              element.style.background = originalStyle.background;
            if (originalStyle.backgroundColor)
              element.style.backgroundColor = originalStyle.backgroundColor;

            // If there were no inline styles but there were computed styles, set those
            if (!originalStyle.background && computedBg !== "none") {
              element.style.background = computedBg;
            }
            if (
              !originalStyle.backgroundColor &&
              computedBgColor !== "rgba(0, 0, 0, 0)"
            ) {
              element.style.backgroundColor = computedBgColor;
            }

            // Restore classes
            element.classList.add(...originalClasses);
          }
        );
      }
    }
  };

  return (
    <Button
      variant="secondary"
      onClick={handleCopy}
      iconComponent={copied ? Check : Copy}
      className={twMerge(
        "flex-shrink-0 flex-grow-0 whitespace-nowrap",
        className
      )}
      data-testid="clipboard-copy-button"
    >
      {copied ? "Copied!" : label}
    </Button>
  );
}

export default ClipboardCopyButton;
